import { z } from "zod";
import { MealSlotSchema } from "./profile.js";
import { PlanJobStatusSchema } from "./planJobs.js";
import { IsoDateSchema, IsoDateTimeSchema, MoneySchema, PlanProposalResponseSchema, ValidationResultSchema, BudgetVerdictSchema, MealServingsSchema } from "./primitives.js";
export const NutritionTotalsSchema = z.object({
    kcal: z.number().int(),
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
});
export const MealInstanceSchema = z.object({
    id: z.string(),
    date: IsoDateSchema,
    slot: MealSlotSchema,
    recipeId: z.string(),
    /** Bilingual recipe title, resolved server-side from the catalog. Null only when the recipe row itself is missing from the catalog — never fabricated. */
    title: z.object({ de: z.string(), en: z.string() }).nullable(),
    servings: z.number().positive(),
    locked: z.boolean(),
    nutrition: NutritionTotalsSchema,
    reasonCodes: z.array(z.string()),
    activeTimeMinutes: z.number().int().nonnegative(),
    totalTimeMinutes: z.number().int().nonnegative(),
    estimatedCostPerServing: MoneySchema,
    batchComponentId: z.string().nullable(),
    /** ADR-077 Category C: a declared-generic decorative image tag (e.g. "dinner/fish"), resolved client-side to a bundled asset — never a claim that this is the exact dish. Null for a recipe with no computed tag yet (resolves through the client's own "generic" fallback). */
    imageTag: z.string().nullable(),
});
export const PlanStateSchema = z.enum(["draft", "accepted", "superseded"]);
// S-2 fix: the newest plan_jobs row for the user (any status), so a client
// can surface "your last regeneration failed" even though the CURRENT plan
// shown is still the last successfully-applied one. Optional/backward-
// compatible — an older client (or a user with no plan_jobs row yet) simply
// never sees the field.
export const PlanLatestJobSchema = z.object({
    id: z.string(),
    status: PlanJobStatusSchema,
    failedReasonCodes: z.array(z.string()).optional(),
    /** Present when status is failed: the job row's authoritative error. */
    failedError: z
        .object({ code: z.string(), details: z.record(z.string(), z.unknown()).optional() })
        .optional(),
    /**
     * Current APPLICABILITY of a failed job (owner invariant 2026-09-04): a
     * persisted failure stays in history, but it is a CURRENT actionable
     * condition only while the constraint it failed on still matches the
     * user's settings. `superseded_by_settings` (ceiling removed / changed /
     * any profile change after the attempt) → clients must NOT render it as
     * "your maximum"; they explain that the plan was built under earlier
     * settings instead. Server-derived by the planning service (the same
     * fence product-state uses) — never inferred from `failedError`.
     */
    applicability: z
        .object({
        status: z.enum(["current", "superseded_by_settings"]),
        reason: z.enum(["ceiling_removed", "ceiling_changed", "settings_changed"]).nullable(),
        constraint: z.enum(["max_checkout"]).nullable(),
        constraintSource: z.enum(["maximum_checkout", "legacy_limit"]).nullable(),
        attemptCeilingCents: z.number().nullable(),
        currentCeilingCents: z.number().nullable(),
        currentCeilingSource: z.enum(["maximum_checkout", "legacy_limit"]).nullable(),
    })
        .optional(),
});
// 2026-09-02 acceptance findings B+C ------------------------------------
// B: a FAILED latest job carries its authoritative error (code + details,
// e.g. BUDGET_INFEASIBLE with budgetCents/cheapestValidCostCents/gapCents)
// so clients render the SPECIFIC deterministic failure — never a generic
// "try again" for a non-retryable cause.
// C: constraintConflicts lists CURRENT-plan meals that violate the user's
// CURRENT hard constraints (never-eat/allergen/dietary/safety exclusion),
// computed server-side with the exact predicate generation enforces. A
// listed meal must never be presented as a valid recommendation.
export const PlanConstraintConflictSchema = z.object({
    mealId: z.string(),
    recipeId: z.string(),
    title: z.string().nullable(),
    reasons: z.array(z.object({
        // ADR-080: a lactose intolerance conflict is reported as its own kind, never as an allergen
        kind: z.enum(["dietary_rule", "never_eat", "safety_exclusion", "allergen", "intolerance"]),
        subjectId: z.string(),
    })),
    /** Ingredient ids to exclude when repairing this meal (client passes verbatim to the proposal). */
    excludeIngredientIds: z.array(z.string()),
});
// GET /v1/plans/current response
// ---------------------------------------------------------------------------
// Step 6 (ADR-043): plan-level grocery-cohesion metrics — honest proxies
// from the SAME pantry-netted, pack-rounded shopping rows as the budget
// verdict (one source of truth). wasteRiskScore is a bounded 0-100
// comparison proxy (documented formula), never a grams-of-waste claim.
// ---------------------------------------------------------------------------
export const PlanWasteMetricsSchema = z.object({
    uniquePurchasedIngredientCount: z.number().int(),
    singleUsePurchasedIngredientCount: z.number().int(),
    singleUsePerishableCount: z.number().int(),
    reusedPurchasedIngredientCount: z.number().int(),
    purchaseReuseRatio: z.number(),
    pantryCoveredIngredientCount: z.number().int(),
    pantryCoverageRatio: z.number(),
    perishableRemainderCount: z.number().int(),
    usualFoodUsageCount: z.number().int(),
    wasteRiskScore: z.number(),
});
export const PlanResponseSchema = z.object({
    id: z.string(),
    revision: z.number().int().nonnegative(),
    /** Correction 2026-08-31: the CURRENT (live) budget verdict for this
     * plan — same rows/prices/pantry semantics as the shopping list, so Amy,
     * Plan, Shop and Fit Score never disagree about "what does my week cost
     * NOW". */
    budget: BudgetVerdictSchema.optional(),
    /** Historical provenance: the verdict recorded when this revision was
     * materialized (prices/pantry at that moment). Debug/audit only — never
     * shown as the current cost. */
    materializedBudgetSnapshot: BudgetVerdictSchema.optional(),
    /** Step-6 correction (ADR-043): LIVE grocery-cohesion metrics in BOTH
     * scopes — `fullWeek` covers every planned meal (historical/accounting),
     * `remainingWeek` only meals dated today (Europe/Berlin) onward (the
     * actionable view fixes and mutation previews reason from). Same
     * authoritative shopping rows as `budget`, different meal scope. */
    planMetrics: z
        .object({
        fullWeek: PlanWasteMetricsSchema,
        remainingWeek: PlanWasteMetricsSchema,
    })
        .optional(),
    weekStartDate: IsoDateSchema,
    state: PlanStateSchema,
    ruleVersion: z.string(),
    dataVersion: z.string(),
    modelVersion: z.string().nullable(),
    meals: z.array(MealInstanceSchema),
    nutritionSummary: NutritionTotalsSchema,
    lastValidation: ValidationResultSchema,
    createdAt: IsoDateTimeSchema,
    latestJob: PlanLatestJobSchema.optional(),
    /** ADR-051 stale-plan recovery state, derived from EXISTING data (the
     * revision's goal snapshot vs the CURRENT profile target): present only
     * when the active plan was generated under different settings. Stale
     * day indexes are the days whose planned nutrition no longer fits the
     * current target band — informational, never silently re-certified;
     * regeneration remains the only certification path. */
    settingsDrift: z
        .object({
        planTargetKcal: z.number(),
        currentTargetKcal: z.number(),
        staleDayIndexes: z.array(z.number().int()),
    })
        .nullable()
        .optional(),
    constraintConflicts: z.array(PlanConstraintConflictSchema).optional(),
});
// POST /v1/plans/{id}/proposals request
/**
 * Step 4 (ADR-039): structured categorical constraints — the reason a
 * categorical mutation exists travels INTO server-side candidate
 * selection (every replacement is filtered against them BEFORE scoring)
 * and OUT as the post-apply proof. Never only natural language.
 */
export const CategoricalConstraintsSchema = z.object({
    excludeIngredientIds: z.array(z.string()).max(50).optional(),
    excludeRecipeIds: z.array(z.string()).max(50).optional(),
    requiredDietaryClasses: z.array(z.enum(["vegan", "vegetarian", "pescatarian", "omnivore"])).max(4).optional(),
});
/**
 * The constraints a PROPOSAL request may carry: the categorical ones above
 * plus a hard cap on the replacement's hands-on time ("I only have 15
 * minutes"). The cap is NOT categorical — it never travels into the apply
 * proof (`constraintProof` stays `CategoricalConstraintsSchema`) and never
 * counts as a "removal" — it only REMOVES candidates from the server's pool
 * before scoring; a request whose pool it empties is an honest 422
 * `NO_FIT_WITHIN_TIME` (never a silently slower dish). Valid only on the
 * intents that replace a recipe (see PlanProposalRequestSchema).
 */
export const ProposalConstraintsSchema = CategoricalConstraintsSchema.extend({
    maxActiveMinutes: z.number().int().min(1).max(600).optional(),
    /**
     * Device pass 2026-10-10: the time the user HAS ("ich hab nur 15 Minuten") —
     * caps the replacement's TOTAL time (hands-on + passive), so a dish with
     * "15 Min. aktiv, 30 Min. gesamt" never answers "nur 15 Minuten". Same
     * hard-cap semantics as `maxActiveMinutes` (never scored, honest
     * NO_FIT_WITHIN_TIME); at most one of the two per request.
     */
    maxTotalMinutes: z.number().int().min(1).max(600).optional(),
});
export const ScopeMatchSchema = z.object({
    mealId: z.string(),
    dayIndex: z.number().int(),
    slot: z.string(),
    recipeId: z.string(),
    matchedBy: z.array(z.string()),
});
export const PlanProposalRequestSchema = z
    .object({
    baseRevision: z.number().int().nonnegative(),
    scope: z.object({ mealIds: z.array(z.string()).min(1) }),
    intent: z.enum([
        "swap",
        "adapt",
        "cheaper",
        "faster",
        "use_pantry",
        "lock",
        "unlock",
        "scale_servings",
        "make_room",
        /**
         * ADR-058: "make this remaining meal lighter so it fits my current
         * day" — ONE deterministic owner: the largest supported portion of the
         * same recipe that fits the live day, else the best fitting eligible
         * swap, else 422 NO_FIT_FOUND with the user-level figures. Exactly one
         * mealId; `objective` optional (default day_range). Never composes.
         */
        "fit_meal",
    ]),
    note: z.string().optional(),
    /**
     * ADR-022 R1: pins the replacement to this exact recipeId (e.g. Amy chat
     * targeting a specific searched/composed recipe) instead of the generic
     * scored candidate search. Optional/backward-compatible — omitted, the
     * proposal path behaves exactly as before. Still fully validator-gated:
     * an unsafe target produces the normal FAIL codes, never a silent
     * downgrade of the swap into "closest safe match" (services/planning's
     * createProposal honors this by skipping its own hardConstraintPreFilter
     * for the targeted candidate and letting validatePlan be the sole gate).
     */
    targetRecipeId: z.string().optional(),
    /** Step 4: categorical constraints enforced on the candidate pool server-side (+ the optional `maxActiveMinutes` time cap). */
    constraints: ProposalConstraintsSchema.optional(),
    /**
     * A1/D-1 fix: required when intent is "scale_servings" — the new
     * serving count for scope.mealIds[0] (exactly one meal id is required
     * for this intent). Replaces the old client convention of encoding
     * `note: "servings:${n}"` on an "adapt" proposal, which the server
     * never actually read — this is a real, explicit, typed change kind so
     * a serving change can never again silently become a recipe swap.
     * services/planning's createProposal scales the SAME recipe's
     * nutrition/cost from its per-serving data; it never substitutes a
     * different recipe for this intent.
     */
    servings: MealServingsSchema.optional(),
    /**
     * ADR-055 acceptance 3 — `make_room` ONLY: which bound the owner makes
     * the ANCHORED meal/serving fit under. `day_range` = the accepted day
     * range (target ± tolerance, what blocks a portion); `target` = the
     * user's exact target. Explicit, never inferred.
     */
    objective: z.enum(["day_range", "target"]).optional(),
})
    .superRefine((val, ctx) => {
    if (val.intent === "make_room") {
        if (val.servings === undefined) {
            ctx.addIssue({ code: "custom", path: ["servings"], message: "servings (the anchored serving) is required when intent is make_room" });
        }
        if (val.scope.mealIds.length !== 1) {
            ctx.addIssue({ code: "custom", path: ["scope", "mealIds"], message: "make_room takes exactly one anchored mealId" });
        }
        if (val.objective === undefined) {
            ctx.addIssue({ code: "custom", path: ["objective"], message: "objective is required when intent is make_room" });
        }
    }
    // The time cap restricts the REPLACEMENT recipe — an intent that keeps the
    // recipe (lock/unlock/scale) or has its own fit rule (fit_meal/make_room)
    // must never receive one it would silently ignore.
    for (const key of ["maxActiveMinutes", "maxTotalMinutes"]) {
        if (val.constraints?.[key] !== undefined &&
            ["lock", "unlock", "scale_servings", "make_room", "fit_meal"].includes(val.intent)) {
            ctx.addIssue({
                code: "custom",
                path: ["constraints", key],
                message: `${key} applies only to intents that replace the recipe (swap, adapt, cheaper, faster, use_pantry)`,
            });
        }
    }
    if (val.constraints?.maxActiveMinutes !== undefined && val.constraints?.maxTotalMinutes !== undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["constraints", "maxTotalMinutes"],
            message: "send one time cap: maxTotalMinutes (the time the user has) or maxActiveMinutes (hands-on time), not both",
        });
    }
    if (val.intent === "fit_meal" && val.scope.mealIds.length !== 1) {
        ctx.addIssue({ code: "custom", path: ["scope", "mealIds"], message: "fit_meal takes exactly one mealId" });
    }
    if (val.intent === "scale_servings") {
        if (val.servings === undefined) {
            ctx.addIssue({
                code: "custom",
                path: ["servings"],
                message: "servings is required when intent is scale_servings",
            });
        }
        if (val.scope.mealIds.length !== 1) {
            ctx.addIssue({
                code: "custom",
                path: ["scope", "mealIds"],
                message: "scale_servings requires exactly one mealId",
            });
        }
    }
});
// ---------------------------------------------------------------------------
// POST /v1/plans/{id}/proposals/candidates — the dishes "Tauschen" offers
// (2026-10-07). READ-ONLY: no proposal row, no confirmation token. Served by
// the SAME pool, scoring and day-window guard the swap proposal uses
// (services/planning createProposal), in the owner's score order, so the list
// and the pinned proposal (`targetRecipeId`) cannot disagree about who is
// eligible. A candidate is NOT validated one by one — the pinned proposal's
// `validatePlan` stays the sole gate, and a candidate it refuses is shown with
// the existing FAIL card; the hard-constraint pre-filter, the categorical
// constraints and the time cap are applied here exactly as for a swap.
// ---------------------------------------------------------------------------
/**
 * Deterministic time fit (2026-10-13, founder decision): "ich hab nur 15 Minuten". The SERVER picks
 * the meal (the named one, else today's first meal in slot order with no recorded outcome), chooses
 * dish and portion through the swap's own pool/guards/budget (maxTotalMinutes), and returns a ready
 * proposal text in both languages. Amy relays the text and asks for a yes; she adds nothing.
 */
export const PlanTimeFitRequestSchema = z.object({
    baseRevision: z.number().int().nonnegative(),
    /** The time the user has, in whole minutes — their WHOLE time (hands-on + waiting). */
    minutes: z.number().int().min(1).max(600),
    /** Only when the user named a meal; otherwise the server picks today's next uneaten meal. */
    mealId: z.string().min(1).optional(),
});
export const PlanTimeFitResponseSchema = z.object({
    /** proposal = a swap to approve; already_fits = the planned dish fits; no_fit = nothing fits (see details); no_meal_left = nothing open today. */
    kind: z.enum(["proposal", "already_fits", "no_fit", "no_meal_left"]),
    mealId: z.string().nullable(),
    /** The ready reply, one per language — relayed as is. */
    presentation: z.object({ de: z.string(), en: z.string() }),
    /** Present when kind = proposal: the stored proposal (apply it with its proposalId + confirmationToken after a yes). */
    proposal: PlanProposalResponseSchema.optional(),
    /** Present when kind = no_fit. */
    details: z
        .object({ reason: z.enum(["no_dish_within_time", "day_calorie_range"]), fastestTotalMinutes: z.number().int().nullable() })
        .optional(),
    /**
     * LB-37 (additive, 2026-10-18): true iff kind = proposal AND a confirmation token exists (the proposal passed
     * validation and can be applied after a yes). Absent on the other kinds. A client or Amy never asks for approval
     * when this is false.
     */
    applyable: z.boolean().optional(),
    /**
     * LB-37 (additive): present when a proposal exists but cannot be applied (validation FAIL, no token). `kind` stays
     * "proposal" so older clients still parse; the presentation then explains the conflict and asks nothing.
     * Codes and subject ids come from `proposal.validation.failures`, de-duplicated — for logs and tools, never shown to users.
     */
    blocked: z.object({ reasonCodes: z.array(z.string()), subjectIds: z.array(z.string()) }).optional(),
});
export const PlanProposalCandidatesRequestSchema = z.object({
    baseRevision: z.number().int().nonnegative(),
    scope: z.object({ mealIds: z.array(z.string()).length(1) }),
    /** 3..5; default 5. Fewer come back only when the eligible pool is smaller. */
    limit: z.number().int().min(3).max(5).optional(),
    constraints: ProposalConstraintsSchema.optional(),
});
export const PlanProposalCandidateSchema = z.object({
    recipeId: z.string(),
    /** Catalog title in both languages — the client shows the one for its locale. */
    title: z.object({ de: z.string(), en: z.string() }),
    activeMinutes: z.number().int().nonnegative(),
    /** Total minutes incl. passive time; equals `activeMinutes` when there is none. */
    totalMinutes: z.number().int().nonnegative(),
    /**
     * Estimated cost of ONE recipe serving in cents — the same figure the
     * pinned proposal's `after.costCents` carries — or `null` when the recipe
     * has no price estimate (render the existing "unavailable" wording, never €0).
     */
    costCents: z.number().int().nonnegative().nullable(),
    /** kcal for the meal's kept serving (per-serving kcal × `servings`) — the same figure the pinned proposal's `after.kcal` carries. */
    kcal: z.number().int().nonnegative(),
    /** The meal's serving count (a swap keeps the portion). */
    servings: z.number().positive(),
});
export const PlanProposalCandidatesResponseSchema = z.object({
    baseRevision: z.number().int().nonnegative(),
    mealId: z.string(),
    /** In the swap's score order, best first; empty = the honest "no alternatives" state. */
    candidates: z.array(PlanProposalCandidateSchema).max(5),
});
// POST /v1/plans/{id}/proposals response — reuses the shared shape.
export { PlanProposalResponseSchema };
// POST /v1/plans/{id}/proposals/{proposalId}/apply request
export const PlanApplyRequestSchema = z.object({
    confirmationToken: z.string(),
    // Budget Model v2 (ADR-052, owner D-2): the pre-v2 `acceptOverBudget`
    // override is GONE. A proposal whose resulting checkout exceeds the hard
    // ceiling (impact.checkoutConflict) is refused with 422
    // MAX_CHECKOUT_CONFLICT; the user changes or removes the maximum instead.
    // Unknown keys sent by unmigrated clients are stripped, never honored.
    baseRevision: z.number().int().nonnegative(),
});
export const PlanMutationResultSchema = z.object({
    plan: PlanResponseSchema,
    shoppingListRevision: z.number().int().nonnegative(),
    pantryReservationsChanged: z.boolean(),
    savingsProjectionUpdated: z.boolean(),
    /**
     * Step 4 post-apply proof: present when the applied proposal carried
     * categorical constraints — the SAME matcher recomputed on the
     * PERSISTED resulting revision from today on. "X is removed" may only
     * be claimed when remainingMatches is empty; never inferred from a 200.
     */
    constraintProof: z
        .object({
        constraints: CategoricalConstraintsSchema,
        fromDayIndex: z.number().int(),
        remainingMatches: z.array(ScopeMatchSchema),
    })
        .optional(),
});
// POST /v1/plans/{id}/undo request
export const PlanUndoRequestSchema = z.object({
    baseRevision: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Step 4 (ADR-039): POST /v1/plans/current/scope — server-side categorical
// scope discovery on the active plan. Strictly read-only.
// ---------------------------------------------------------------------------
export const PlanScopeRequestSchema = z
    .object({
    ingredientNames: z.array(z.string().min(1).max(200)).max(20).optional(),
    ingredientIds: z.array(z.string()).max(20).optional(),
    recipeTitles: z.array(z.string().min(1).max(200)).max(20).optional(),
    recipeIds: z.array(z.string()).max(20).optional(),
    requiredDietaryClasses: z.array(z.enum(["vegan", "vegetarian", "pescatarian", "omnivore"])).max(4).optional(),
    includePastDays: z.boolean().optional(),
})
    .refine((b) => (b.ingredientNames?.length ?? 0) +
    (b.ingredientIds?.length ?? 0) +
    (b.recipeTitles?.length ?? 0) +
    (b.recipeIds?.length ?? 0) +
    (b.requiredDietaryClasses?.length ?? 0) >
    0, { message: "at least one scope criterion required" });
export const PlanScopeResponseSchema = z.object({
    planId: z.string(),
    baseRevision: z.number().int(),
    fromDayIndex: z.number().int(),
    resolution: z.object({
        ingredients: z.array(z.object({ needle: z.string(), ingredientIds: z.array(z.string()) })),
        recipes: z.array(z.object({
            needle: z.string(),
            matches: z.array(z.object({ recipeId: z.string(), title: z.string() })),
            /** Distinct recipes match the same name — clarify, never silently exclude the wrong one. */
            ambiguous: z.boolean(),
        })),
        ambiguous: z.boolean(),
    }),
    /** Ready to pass as PlanProposalRequest.constraints (ambiguous needles contribute nothing). */
    constraints: CategoricalConstraintsSchema,
    matches: z.array(ScopeMatchSchema.extend({ recipeTitle: z.record(z.string(), z.string()).nullable() })),
});
// ---------------------------------------------------------------------------
// ADR-075 (post-action truth): GET /v1/plans/current/changes — the server-
// computed CHANGE PROOF of a plan version vs its parent (rebuild, proposal
// apply or undo alike), computed on read from the immutable revision chain.
// The rebuild analogue of constraintProof: "your week was rebuilt around X"
// may only be claimed from THIS object — never inferred from a completed
// job. Deliberately absent from the wire: revision numbers and ids
// (`isCurrent` says whether the compared version is still the active one).
// ---------------------------------------------------------------------------
export const PlanChangeKindSchema = z.enum(["replaced", "portion", "added", "removed"]);
const BilingualTitleSchema = z.object({ de: z.string(), en: z.string() }).nullable();
export const PlanChangedMealSideSchema = z.object({
    recipeId: z.string(),
    /** Catalog title; null only when the recipe row is missing — never fabricated. */
    title: BilingualTitleSchema,
    servings: z.number().positive(),
});
export const PlanChangedMealSchema = z.object({
    /** Stable `${slot}_d${dayIndex}` of the AFTER meal (the BEFORE meal when kind = removed). */
    mealId: z.string(),
    date: IsoDateSchema,
    slot: MealSlotSchema,
    /** replaced = a different recipe in this (date, slot); portion = same recipe, different servings; added/removed = the slot exists on one side only. Lock state is never a change. */
    kind: PlanChangeKindSchema,
    before: PlanChangedMealSideSchema.nullable(),
    after: PlanChangedMealSideSchema.nullable(),
});
export const PlanIngredientUseMealSchema = z.object({
    mealId: z.string(),
    date: IsoDateSchema,
    slot: MealSlotSchema,
    title: BilingualTitleSchema,
});
export const PlanIngredientUseSchema = z.object({
    ingredientId: z.string(),
    displayName: BilingualTitleSchema,
    /** Meals (in scope) whose recipe lists this ingredient — the ADR-039 scope matcher, exact stable id. */
    mealsBefore: z.array(PlanIngredientUseMealSchema),
    mealsAfter: z.array(PlanIngredientUseMealSchema),
});
/** ADR-076: the variety facts of one plan version (whole plan, batch-fed slots never counted as repetition). */
export const PlanVarietySummarySchema = z.object({
    mealCount: z.number().int().nonnegative(),
    distinctRecipes: z.number().int().nonnegative(),
    /** Days d where a non-batch lunch/dinner recipe of day d is a lunch/dinner again on day d + 1. */
    adjacentDayMainRepeats: z.number().int().nonnegative(),
    /** Adjacent day pairs whose populated slots all carry the same recipes. */
    identicalAdjacentDays: z.number().int().nonnegative(),
    /** Max over adjacent day pairs of (matching slots ÷ slots populated on both days), 0–1. */
    daySimilarityMax: z.number().min(0).max(1),
});
export const PlanChangeProofSchema = z.object({
    planId: z.string(),
    comparedTo: z.enum(["previous_plan", "no_previous_plan"]),
    /** True while the compared (AFTER) version is still the active plan. */
    isCurrent: z.boolean(),
    /** The plan job that produced the AFTER version, when one did (rebuilds); null for proposal applies and undo. */
    job: z
        .object({
        id: z.string(),
        status: PlanJobStatusSchema,
        optimizationPriority: z.enum(["balanced", "low_waste", "high_variety"]).nullable(),
    })
        .nullable(),
    /** Remaining-week scope by default: meals dated today (Europe/Berlin) onward. */
    scope: z.object({ fromDate: IsoDateSchema, includePastDays: z.boolean() }),
    /** Deterministic: "changed" iff at least one meal in scope differs. */
    outcome: z.enum(["changed", "unchanged"]),
    changedMeals: z.array(PlanChangedMealSchema),
    unchangedMealCount: z.number().int().nonnegative(),
    ingredientUse: z.array(PlanIngredientUseSchema),
    /** Null when no ingredient could be evaluated; else whether the requested ingredients are used in MORE meals after than before. */
    requestedIngredientsUsedMore: z.boolean().nullable(),
    /** Requested ids the catalog does not know — reported, never silently dropped. */
    unresolvedIngredientIds: z.array(z.string()),
    /** Both sides through the ONE live shopping preview (same pantry, same prices — apples to apples about the meals); null when the preview was unavailable. */
    shopping: z
        .object({
        status: z.enum(["estimated", "unknown"]),
        rowsToBuyBefore: z.number().int().nonnegative().nullable(),
        rowsToBuyAfter: z.number().int().nonnegative().nullable(),
        checkoutCentsBefore: z.number().int().nonnegative().nullable(),
        checkoutCentsAfter: z.number().int().nonnegative().nullable(),
    })
        .nullable(),
    /** Remaining-week cohesion metrics on both sides — explanations for waste questions, never evidence of change. */
    metrics: z
        .object({ before: PlanWasteMetricsSchema.nullable(), after: PlanWasteMetricsSchema.nullable() })
        .nullable(),
    /**
     * ADR-076: the week's variety on both sides, computed from the persisted
     * meals (the same definitions the planner's quality gate uses). Evidence
     * for "kept the week varied" — never volunteered; absent on older
     * servers.
     */
    variety: z
        .object({
        before: PlanVarietySummarySchema.nullable(),
        after: PlanVarietySummarySchema,
        /** True when a variety rule had to fall back to the full pool for this version (from the plan job's record; null when unknown). */
        varietyDegraded: z.boolean().nullable(),
    })
        .optional(),
});
export const PlanChangesQuerySchema = z.object({
    /** Proof of THAT job's result version vs its parent (even if since superseded). Default: the active version vs its parent. */
    jobId: z.string().optional(),
    /** Comma-separated stable ingredient ids (max 20). Default: the user's currently available Kitchen items. */
    ingredientIds: z.string().max(2000).optional(),
    includePastDays: z.enum(["true", "false"]).optional(),
});
// ---------------------------------------------------------------------------
// Step 4 (ADR-039): durable recipe exclusions — "don't plan X again".
// Reversible (excluded: false → status removed), stable recipe identity.
// ---------------------------------------------------------------------------
export const RecipeExclusionPutRequestSchema = z.object({
    recipeId: z.string().min(1).max(200),
    excluded: z.boolean(),
});
export const RecipeExclusionListResponseSchema = z.object({
    exclusions: z.array(z.object({
        recipeId: z.string(),
        title: z.record(z.string(), z.string()).nullable(),
        createdAt: z.string().nullable(),
    })),
});
// ---------------------------------------------------------------------------
// Step 5A (ADR-041): observed usual-foods digest — deterministic
// frequency+recency over CONFIRMED behavior (meal outcomes, pantry adds).
// Provenance is always "observed"; a positive scoring signal, never a gate.
// ---------------------------------------------------------------------------
export const UsualFoodEntrySchema = z.object({
    identity: z.string(),
    title: z.record(z.string(), z.string()).nullable(),
    score: z.number(),
    occurrences: z.number().int(),
    distinctDays: z.number().int(),
    lastSeenDaysAgo: z.number().int(),
    provenance: z.literal("observed"),
});
export const UsualFoodsResponseSchema = z.object({
    ingredients: z.array(UsualFoodEntrySchema),
    recipes: z.array(UsualFoodEntrySchema),
});
//# sourceMappingURL=plans.js.map